import { useEffect, useMemo } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import type { SupplierNews } from '../../types'

export type MapMarkerSeverity = 'critical' | 'high' | 'medium' | 'low' | 'other'

export interface GeographicRiskMapItem {
  id: string
  lat: number
  lng: number
  headline?: string
  region?: string | null
  /** Task severity/priority — drives pin color. */
  severity?: string | null
}

interface GeographicRiskMapProps {
  items: GeographicRiskMapItem[]
  onSelectItem: (item: SupplierNews | GeographicRiskMapItem) => void
  title?: string
  subtitle?: string
  loading?: boolean
}

function normalizeSeverity(raw: string | null | undefined): MapMarkerSeverity {
  const s = (raw || '').toLowerCase()
  if (s === 'critical') return 'critical'
  if (s === 'high') return 'high'
  if (s === 'medium' || s === 'moderate') return 'medium'
  if (s === 'low') return 'low'
  return 'other'
}

function getMarkerIcon(severity: MapMarkerSeverity): L.DivIcon {
  return L.divIcon({
    className: 'custom-signal-marker',
    html: `<div class="es-map-marker es-map-marker--${severity}" style="width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.25);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  })
}

const markerIconBySeverity: Record<MapMarkerSeverity, L.DivIcon> = {
  critical: getMarkerIcon('critical'),
  high: getMarkerIcon('high'),
  medium: getMarkerIcon('medium'),
  low: getMarkerIcon('low'),
  other: getMarkerIcon('other'),
}

function FitBounds({ items }: { items: GeographicRiskMapItem[] }) {
  const map = useMap()
  const bounds = useMemo(() => {
    if (items.length === 0) return null
    const lats = items.map((i) => i.lat)
    const lngs = items.map((i) => i.lng)
    return L.latLngBounds(
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)]
    )
  }, [items])
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 10 })
  }, [map, bounds])
  return null
}

function MapResizeHandler() {
  const map = useMap()

  useEffect(() => {
    const handleResize = () => {
      map.invalidateSize()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [map])

  return null
}

export function GeographicRiskMap({
  items,
  onSelectItem,
  title = 'Open mitigation actions',
  subtitle = 'Mapped open mitigation actions',
  loading = false,
}: GeographicRiskMapProps) {
  return (
    <div
      className="rounded-[var(--radius-lg)] border border-es-soft bg-es-haze overflow-hidden relative shadow-[var(--shadow-card)]"
      style={{ height: 480 }}
    >
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: '100%', width: '100%' }}
        className="rounded-[var(--radius-lg)]"
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapResizeHandler />
        {items.length > 0 && <FitBounds items={items} />}
        {items.map((item) => {
          const severity = normalizeSeverity(item.severity)
          return (
            <Marker
              key={item.id}
              position={[item.lat, item.lng]}
              icon={markerIconBySeverity[severity]}
              eventHandlers={{
                click: () => onSelectItem(item),
              }}
            >
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold text-es-navy">{item.headline || 'Mitigation'}</div>
                  {item.region && <div className="text-es-muted mt-0.5">{item.region}</div>}
                  <button
                    type="button"
                    onClick={() => onSelectItem(item)}
                    className="block mt-2 text-es-signal hover:text-es-deep hover:underline text-xs font-medium"
                  >
                    View details
                  </button>
                </div>
              </Popup>
            </Marker>
          )
        })}
      </MapContainer>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-[1000] bg-es-haze/90 rounded-[var(--radius-lg)]">
          <i className="fa-solid fa-spinner fa-spin text-4xl text-es-signal" aria-hidden />
          <span className="sr-only">Loading map...</span>
        </div>
      )}

      <div className="absolute top-3 left-3 z-[500] bg-es-white/95 border border-es-soft rounded-[var(--radius-md)] px-3 py-2 shadow-[var(--shadow-card)] max-w-[min(100%,20rem)]">
        <div className="text-xs font-bold text-es-navy uppercase tracking-wider">{title}</div>
        {subtitle && <div className="text-[11px] text-es-muted mt-0.5">{subtitle}</div>}
        <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-es-muted">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-es-riskHigh" aria-hidden /> Critical
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-es-riskHigh" aria-hidden /> High
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-es-riskMedium" aria-hidden /> Medium
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-es-riskLow" aria-hidden /> Low
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-es-signal" aria-hidden /> Other
          </span>
        </div>
      </div>
    </div>
  )
}
